import { useState, useEffect, useMemo } from 'react';
import { format, eachDayOfInterval, isWeekend, differenceInDays, parseISO } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  CalendarIcon, 
  Clock, 
  AlertTriangle, 
  ArrowRight, 
  ArrowLeft,
  Users,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCompanyProfiles, useTimesheetMutations } from '@/hooks/timesheets';
import { timesheetService } from '@/services/timesheets/timesheetService';
import type { BulkTimeEntry } from '@/services/timesheets/types';

interface BulkTimeEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'configure' | 'preview';

const MAX_DATE_RANGE_DAYS = 90;

export function BulkTimeEntryDialog({ open, onOpenChange }: BulkTimeEntryDialogProps) {
  const [step, setStep] = useState<Step>('configure');
  
  // Configuration state
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [clockInTime, setClockInTime] = useState('09:00');
  const [clockOutTime, setClockOutTime] = useState('17:00');
  const [weekdaysOnly, setWeekdaysOnly] = useState(true);
  const [status, setStatus] = useState<'pending' | 'approved'>('pending');
  const [reason, setReason] = useState('');

  // Queries and mutations
  const { data: profiles = [] } = useCompanyProfiles();
  const { createBulkEntries } = useTimesheetMutations();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep('configure');
      setSelectedEmployee('');
      setStartDate(undefined);
      setEndDate(undefined);
      setClockInTime('09:00');
      setClockOutTime('17:00');
      setWeekdaysOnly(true);
      setStatus('pending');
      setReason('');
    }
  }, [open]);

  // Calculate all potential entries based on configuration
  const allPotentialEntries = useMemo<BulkTimeEntry[]>(() => {
    if (!startDate || !endDate) return [];
    
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    return days
      .filter(day => !weekdaysOnly || !isWeekend(day))
      .map(day => ({
        date: format(day, 'yyyy-MM-dd'),
        clock_in_time: clockInTime,
        clock_out_time: clockOutTime,
      }));
  }, [startDate, endDate, clockInTime, clockOutTime, weekdaysOnly]);

  // Fetch conflicts when entering preview step
  const { data: conflicts = [], isLoading: isLoadingConflicts } = useQuery({
    queryKey: ['bulk-entry-conflicts', selectedEmployee, allPotentialEntries.map(e => e.date)],
    queryFn: () => timesheetService.checkBulkEntryConflicts(
      selectedEmployee,
      allPotentialEntries.map(e => e.date)
    ),
    enabled: step === 'preview' && selectedEmployee !== '' && allPotentialEntries.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Set of dates with conflicts for quick lookup
  const conflictDates = useMemo(() => {
    return new Set(conflicts.map(c => c.date));
  }, [conflicts]);

  // Split entries into those that will be created vs skipped
  const entriesToCreate = useMemo(() => {
    return allPotentialEntries.filter(entry => !conflictDates.has(entry.date));
  }, [allPotentialEntries, conflictDates]);

  const entriesToSkip = useMemo(() => {
    return allPotentialEntries.filter(entry => conflictDates.has(entry.date));
  }, [allPotentialEntries, conflictDates]);

  // Calculate hours per entry
  const hoursPerEntry = useMemo(() => {
    const [inH, inM] = clockInTime.split(':').map(Number);
    const [outH, outM] = clockOutTime.split(':').map(Number);
    const inMinutes = inH * 60 + inM;
    const outMinutes = outH * 60 + outM;
    const diffMinutes = outMinutes - inMinutes;
    if (diffMinutes <= 0) return 0;
    return Math.round((diffMinutes / 60) * 100) / 100;
  }, [clockInTime, clockOutTime]);

  // Validation checks
  const isRetroactive = useMemo(() => {
    if (!startDate) return false;
    return differenceInDays(new Date(), startDate) > 30;
  }, [startDate]);

  const dateRangeTooLarge = useMemo(() => {
    if (!startDate || !endDate) return false;
    return differenceInDays(endDate, startDate) > MAX_DATE_RANGE_DAYS;
  }, [startDate, endDate]);

  const isTimeValid = hoursPerEntry > 0;
  const isReasonValid = reason.trim().length > 0;
  const isEmployeeSelected = selectedEmployee !== '';
  const hasEntries = allPotentialEntries.length > 0;

  const canProceedToPreview = 
    isEmployeeSelected && 
    hasEntries && 
    isTimeValid && 
    isReasonValid && 
    !dateRangeTooLarge;

  const selectedProfile = profiles.find(p => p.id === selectedEmployee);

  const handleCreate = async () => {
    if (!selectedEmployee || entriesToCreate.length === 0) return;

    createBulkEntries.mutate(
      {
        profileId: selectedEmployee,
        entries: entriesToCreate,
        reason: reason.trim(),
        status,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Add Bulk Time Entries
            <span className="text-sm font-normal text-muted-foreground ml-2">
              — Step {step === 'configure' ? '1' : '2'} of 2
            </span>
          </DialogTitle>
          <DialogDescription>
            {step === 'configure' 
              ? 'Configure time entries to create for an employee'
              : 'Review and confirm the entries to be created'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-6">
          {step === 'configure' && (
            <>
              {/* Employee Selection */}
              <div className="space-y-2">
                <Label htmlFor="employee">Employee *</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {profile.first_name} {profile.last_name}
                          <span className="text-muted-foreground text-xs">({profile.email})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !startDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !endDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) => date > new Date() || (startDate && date < startDate)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {dateRangeTooLarge && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Date range cannot exceed {MAX_DATE_RANGE_DAYS} days. Please select a smaller range.
                  </AlertDescription>
                </Alert>
              )}

              {/* Time Inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clock-in-time">Clock In Time *</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="clock-in-time"
                      type="time"
                      value={clockInTime}
                      onChange={(e) => setClockInTime(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clock-out-time">Clock Out Time *</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="clock-out-time"
                      type="time"
                      value={clockOutTime}
                      onChange={(e) => setClockOutTime(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {!isTimeValid && clockInTime && clockOutTime && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    Clock out time must be after clock in time.
                  </AlertDescription>
                </Alert>
              )}

              {/* Options */}
              <div className="flex items-center justify-between py-2 border-y">
                <div className="space-y-0.5">
                  <Label htmlFor="weekdays-only">Weekdays Only</Label>
                  <p className="text-sm text-muted-foreground">
                    Skip Saturday and Sunday
                  </p>
                </div>
                <Switch
                  id="weekdays-only"
                  checked={weekdaysOnly}
                  onCheckedChange={setWeekdaysOnly}
                />
              </div>

              {/* Status Selection */}
              <div className="space-y-2">
                <Label>Entry Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as 'pending' | 'approved')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">Pending</Badge>
                        <span className="text-muted-foreground text-xs">Requires approval</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="approved">
                      <div className="flex items-center gap-2">
                        <Badge variant="default">Approved</Badge>
                        <span className="text-muted-foreground text-xs">Immediately approved</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label htmlFor="reason">Reason *</Label>
                <Textarea
                  id="reason"
                  placeholder="Explain why these entries are being created (e.g., 'Employee missed clock-ins due to system issues')"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
                {!isReasonValid && reason === '' && (
                  <p className="text-sm text-muted-foreground">
                    A reason is required for audit purposes
                  </p>
                )}
              </div>
            </>
          )}

          {step === 'preview' && (
            <>
              {/* Loading state for conflict check */}
              {isLoadingConflicts && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Checking for conflicts...</span>
                </div>
              )}

              {!isLoadingConflicts && (
                <>
                  {/* Summary Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-primary" />
                        Entry Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Employee:</span>
                          <p className="font-medium">
                            {selectedProfile?.first_name} {selectedProfile?.last_name}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Status:</span>
                          <p>
                            <Badge variant={status === 'approved' ? 'default' : 'secondary'}>
                              {status}
                            </Badge>
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Date Range:</span>
                          <p className="font-medium">
                            {startDate && format(startDate, 'MMM d')} - {endDate && format(endDate, 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Time:</span>
                          <p className="font-medium">{clockInTime} - {clockOutTime}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Entries to Create:</span>
                          <p className="font-medium text-lg text-primary">{entriesToCreate.length}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Total Hours:</span>
                          <p className="font-medium text-lg">
                            {(entriesToCreate.length * hoursPerEntry).toFixed(1)}h
                          </p>
                        </div>
                      </div>
                      <div className="pt-2 border-t">
                        <span className="text-muted-foreground text-sm">Reason:</span>
                        <p className="text-sm mt-1">{reason}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Conflict Warning */}
                  {entriesToSkip.length > 0 && (
                    <Alert className="border-amber-500/50 bg-amber-500/10">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 dark:text-amber-200">
                        <strong>{entriesToSkip.length} date(s) will be skipped</strong> because entries already exist.
                        These are shown below with a conflict indicator.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Retroactive Warning */}
                  {isRetroactive && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Some selected dates are more than 30 days old and may be in a processed payroll period.
                        These entries might require manual payroll adjustment.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* No valid entries warning */}
                  {entriesToCreate.length === 0 && (
                    <Alert variant="destructive">
                      <XCircle className="h-4 w-4" />
                      <AlertDescription>
                        All selected dates have existing entries. No new entries will be created.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Entry List Preview */}
                  <div className="space-y-2">
                    <Label>
                      All Dates ({allPotentialEntries.length})
                      {entriesToSkip.length > 0 && (
                        <span className="ml-2 text-muted-foreground font-normal">
                          • <span className="text-primary">{entriesToCreate.length} will create</span>
                          • <span className="text-amber-600">{entriesToSkip.length} will skip</span>
                        </span>
                      )}
                    </Label>
                    <div className="border rounded-lg max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-background border-b">
                          <tr>
                            <th className="text-left p-2 font-medium">Date</th>
                            <th className="text-left p-2 font-medium">Day</th>
                            <th className="text-left p-2 font-medium">Time</th>
                            <th className="text-right p-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allPotentialEntries.slice(0, 50).map((entry, index) => {
                            const date = parseISO(entry.date);
                            const hasConflict = conflictDates.has(entry.date);
                            return (
                              <tr 
                                key={index} 
                                className={cn(
                                  "border-b last:border-0",
                                  hasConflict && "bg-amber-500/5"
                                )}
                              >
                                <td className="p-2">{format(date, 'MMM d, yyyy')}</td>
                                <td className="p-2 text-muted-foreground">{format(date, 'EEE')}</td>
                                <td className="p-2">{entry.clock_in_time} - {entry.clock_out_time}</td>
                                <td className="p-2 text-right">
                                  {hasConflict ? (
                                    <Badge variant="outline" className="border-amber-500 text-amber-600">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      Conflict
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="border-primary text-primary">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Will Create
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {allPotentialEntries.length > 50 && (
                        <p className="p-2 text-sm text-muted-foreground text-center">
                          ...and {allPotentialEntries.length - 50} more entries
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0">
          {step === 'configure' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => setStep('preview')} 
                disabled={!canProceedToPreview}
              >
                Preview
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep('configure')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={createBulkEntries.isPending || isLoadingConflicts || entriesToCreate.length === 0}
              >
                {createBulkEntries.isPending ? 'Creating...' : `Create ${entriesToCreate.length} Entries`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
